import Vue from 'vue';
import HTTP from '@/utils/http';
import crudableFactory from '@/store/modules/mixins/crudable';
import shareableResource from '@/store/modules/mixins/shareableResource';

const crudable = crudableFactory();
const defaultState = () => ({
  ...crudable.state,
  selected: {
    sharing: {
      groups: [],
      users: [],
    },
  },
  saving: false,
});

const templateModule = {
  namespaced: true,
  state: defaultState(),
  mutations: {
    ...crudable.mutations,
    RESET_STATE(state) {
      Object.assign(state, defaultState());
    },
    SET(state, { updated }) {
      state.selected = updated;
    },
    SET_SAVING(state, saving) {
      state.saving = saving;
    },
    DELETE_FIELD(state, { id }) {
      const fields = state.selected.fields.filter(item => item.id !== id);
      Vue.set(state.selected, 'fields', fields);
    },
  },
  actions: {
    RESET_STATE({ commit }) {
      commit('RESET_STATE');
    },
    GET: crudable.actions.getPartialApplication({
      uriRoot: 'templates',
    }),
    UPDATE: crudable.actions.updatePartialApplication({
      uriRoot: 'templates',
      name: 'template',
      props: ['title', 'widget_return_url'],
      isSingular: true,
    }),
    UPDATE_SHARING: crudable.actions.updatePartialApplication({
        uriRoot: 'templates',
        name: 'template',
        props: ['sharing'],
        isSingular: true,
        emitGlobalMessage: false,
    }),
    async REPLACE({ dispatch }, { file, fingerprint: oldFingerprint }) {
      const formData = new FormData();
      formData.append('document', file);

      const { data: { data: { fingerprint } } } = await HTTP({
        method: 'POST',
        url: `templates/${oldFingerprint}/document`,
        contentType: 'application/x-www-form-urlencoded',
        data: formData,
      });

      await dispatch('WAIT_UNTIL_PROCESSED', { fingerprint });
    },
    async CREATE({ dispatch }, { file }) {
      const formData = new FormData();
      formData.append('document', file);

      const { data: { data: { fingerprint } } } = await HTTP({
        method: 'POST',
        url: 'templates',
        contentType: 'application/x-www-form-urlencoded',
        data: formData,
      });

      await dispatch('WAIT_UNTIL_PROCESSED', { fingerprint });
    },
    async WAIT_UNTIL_PROCESSED({ dispatch, getters }, { fingerprint }) {
      await dispatch('GET', { id: fingerprint });
      const template = getters.selected;

      if (!template.processed_at) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        await dispatch('WAIT_UNTIL_PROCESSED', { fingerprint });
      }

      return template;
    },
    TRANSFER({ commit }, { fingerprint, userId }) {
      return HTTP({
        method: 'put',
        url: `templates/${fingerprint}`,
        data: {
          user_id: userId,
        },
      }).then((response) => {
        commit('SET', {
          updated: response.data.data,
        });
        return response.data.data;
      });
    },
    COPY({ commit }, fingerprint) {
      return HTTP({
        method: 'post',
        url: `templates/${fingerprint}/copy`,
      }).then((response) => {
        commit('SET', {
          updated: response.data.data,
        });
        return response.data.data;
      });
    },
    DELETE({ commit }, { fingerprint }) {
      return HTTP({
        method: 'delete',
        url: `templates/${fingerprint}`,
      }).then((response) => {
        commit('SET', {
          updated: response.data.data,
        });
        return response.data.data;
      });
    },
    ADD_PARTIES({ commit }, { parties, fingerprint }) {
      return HTTP({
        method: 'post',
        url: `templates/${fingerprint}/parties`,
        data: {
          parties,
        },
      }).then((response) => {
        const obj = response.data.data;
        commit('SET', { updated: obj });
        return obj;
      });
    },
    UPDATE_PARTY({ commit }, { party, fingerprint }) {
      return HTTP({
        method: 'put',
        url: `templates/${fingerprint}/parties/${party.id}`,
        data: {
          title: party.title,
          role: party.role,
        },
      }).then((response) => {
        commit('SET', { updated: response.data.data });
        return response.data;
      });
    },
    DELETE_PARTY({ commit }, { party, fingerprint }) {
      return HTTP({
        method: 'delete',
        url: `templates/${fingerprint}/parties/${party.id}`,
      }).then((response) => {
        commit('SET', { updated: response.data.data });
        return response.data;
      });
    },
    ADD_FIELDS({ getters, commit }, { fields }) {
      const document = getters.selected;
      const envelopeFields = [];
      fields.forEach((field) => {
        const contractId = document.id;
        const partyId = field.party.id;
        const defaultHeight = field.type === 'checkbox' ? 2 : 3;
        const defaultWidth = field.type === 'checkbox' ? 3 : 23;
        const newField = {
          contract_id: contractId,
          party_id: partyId,
          page: field.page,
          type: field.type,
          font: field.font,
          group: field.group,
          choices: !field.choices.length && field.type === 'dropdown' ? [{ name: '--Default Option--' }] : field.choices,
          title: field.title || 'Untitled',
          required: field.required || false,
          top: field.top,
          left: field.left,
          x: field.x,
          y: field.y,
          height: field.height ? field.height : `${defaultHeight}%`,
          width: field.width ? field.width : `${defaultWidth}%`,
          merge_field: field.merge_field,
        };
        envelopeFields.push(newField);
      });

      commit('SET_SAVING', true);

      return HTTP({
        method: 'post',
        url: `templates/${getters.selected.fingerprint}/fields`,
        data: {
          fields: envelopeFields,
        },
      }).then((response) => {
        const obj = response.data.data;
        commit('SET', { updated: response.data.data });
        commit('SET_SAVING', false);
        return obj;
      }).catch(() => {
        commit('SET_SAVING', false);
      });
    },
    UPDATE_FIELDS({ commit, getters }, { fields }) {
      const { fingerprint } = getters.selected;
      const document = getters.selected;
      const envelopeFields = [];
      let missingIds;

      for (let i = 0; i < fields.length; i += 1) {
        const field = fields[i];
        const {
          width, height, left, top, required, title, page, id, group, choices,
        } = field;
        const contractId = document.id;
        const partyId = field.party_id; // TODO Simplify the internal data structure

        missingIds = partyId === 0 || id === 0;

        if (missingIds) break;

        // convert the font to uppercase
        const font = field.font.charAt(0).toUpperCase() + field.font.slice(1);

        const updateField = {
          contract_id: contractId,
          party_id: partyId,
          id,
          title,
          required,
          top,
          left,
          height,
          width,
          font,
          page,
          group,
          choices: !choices.length && field.type === 'dropdown' ? [{ name: '--Default Option--' }] : choices,
          merge_field: field.merge_field,
        };
        envelopeFields.push(updateField);
      }

      if (missingIds) return false;

      commit('SET_SAVING', true);


      return HTTP({
        method: 'put',
        url: `templates/${fingerprint}/fields`,
        data: {
          fields: envelopeFields,
        },
      }).then((response) => {
        const obj = response.data.data;
        commit('SET', { updated: obj });
        commit('SET_SAVING', false);
        return obj;
      }).catch(() => {
        commit('SET_SAVING', false);
      });
    },
    DELETE_FIELDS({ commit, getters }, { fields }) {
      const { fingerprint, parties } = getters.selected;
      const document = getters.selected;
      const envelopeFields = [];

      fields.forEach((field) => {
        const contractId = document.id;
        const fieldNo = field.party.number ? field.party.number - 1 : field.party.order;
        const partyId = parties[fieldNo].id;
        const deleteField = {
          id: field.id,
          contract_id: contractId,
          party_id: partyId,
        };
        envelopeFields.push(deleteField);
        commit('DELETE_FIELD', { contractId, id: field.id });
      });

      commit('SET_SAVING', true);

      return HTTP({
        method: 'delete',
        url: `templates/${fingerprint}/fields`,
        data: {
          fields: envelopeFields,
        },
      }).then((response) => {
        const obj = response.data.data;
        commit('SET', { updated: obj });
        commit('SET_SAVING', false);
        return obj;
      }).catch(() => {
        commit('SET_SAVING', false);
      });
    },

  },
  getters: {
    ...crudable.getters,
    ...shareableResource.getters,
    selected: state => state.selected,
    saving: state => state.saving,
    fields: (state, getters) => {
      const template = getters.selected;
      const { parties } = template;
      const fields = [];

      if (!template.fields) {
        return [];
      }

      function getParty(id, array) {
        const party = array.filter(item => item.id === id)[0];
        array.forEach((item, index) => {
          if (party.id === item.id) {
            party.order = index;
          }
        });
        return party;
      }

      fields.push(...template.fields.filter((field) => {
        Vue.set(field, 'party', getParty(field.party_id, parties));
        return field;
      }));

      return fields;
    },
  },
};

export default templateModule;
